import { useEffect, useState } from "react";
import { Box, Divider, Fade, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { EquipmentCategory } from "../../features/FloorManager/typings/svg.types";
import EditableCategoryList from "../../components/Category/editable-category-list.component";
import FilterableEquipmentList from "../../components/EquipmentList/FilterableEquipmentList/filterable-equipment-list";
import { useRemoteFetchCategoryPerType } from "../../hooks/Remote/Category/useRemoteFetchCategoryPerType";
import EditableBookingPropertyList from "../../components/BookingPropertyList/editable-booking-property-list.component";
import { a11yProps } from "../../features/TabPanel/TabPanel.partial";
import { TabPanel } from "../../features/TabPanel/TabPanel.component";
import { CustomColoredTabs } from "../CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../CustomColoredTabs/CustomColoredTab.component";
/**
 * @version 0.2.0
 */
const InventoryManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    userRoles: { isFleetAdmin, isDeviceResponsible }
  } = useSelector((state: RootState) => state.login);
  const isFleetOnlyPropertyAccess = isFleetAdmin && !isDeviceResponsible;

  const [equipmentCategories, setEquipmentCategories] = useState<EquipmentCategory[]>([]);

  const [tabState, setTabState] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent<{}>, newValue: number) =>
    setTabState(newValue);

  const { data: remoteCategories, isLoading: isCategoriesLoading } = useRemoteFetchCategoryPerType({
    type: "equipment-category"
  });

  useEffect(() => {
    if (!remoteCategories) return;
    setEquipmentCategories(remoteCategories);
  }, [isCategoriesLoading, remoteCategories]);

  if (isFleetOnlyPropertyAccess) {
    return (
      <Fade in>
        <Box
          sx={{ "& > *": { margin: theme.spacing(1) } }}
          data-testid="inventory-mgmt-tabs-parent"
        >
          <EditableBookingPropertyList initialPropertyTypes={["fleet"]} />
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in>
      <Box sx={{ "& > *": { margin: theme.spacing(1) } }} data-testid="inventory-mgmt-tabs-parent">
        <CustomColoredTabs value={tabState} onChange={handleTabChange} autoScrollable>
          <CustomColoredTab
            index={0}
            data-testid={"tab1"}
            label={t("Manage equipment")}
            {...a11yProps(0)}
          />
          <CustomColoredTab
            index={0}
            data-testid={"tab2"}
            label={t("Manage category")}
            {...a11yProps(1)}
          />
          <CustomColoredTab
            index={0}
            data-testid={"tab3"}
            label={t("Manage property")}
            {...a11yProps(2)}
          />
        </CustomColoredTabs>

        {/* list of filterable equipment inventories */}
        <TabPanel value={tabState} index={0}>
          <FilterableEquipmentList equipmentCategories={equipmentCategories} />
        </TabPanel>

        <TabPanel value={tabState} index={1}>
          {/* list of equipment categories */}
          <EditableCategoryList
            categoryType="equipment-category"
            usedHeadlineKey="Equipment Category"
          />
          <Divider sx={{ opacity: "0.6" }} />

          {/* list of place categories */}
          <EditableCategoryList categoryType="place-category" usedHeadlineKey="Place Category" />
          <Divider sx={{ opacity: "0.6" }} />

          {/* list of zone categories */}
          <EditableCategoryList categoryType="zone-category" usedHeadlineKey="Zone Category" />
          <Divider sx={{ opacity: "0.6" }} />
        </TabPanel>

        {/* list of booking properties */}
        <TabPanel value={tabState} index={2}>
          <EditableBookingPropertyList />
        </TabPanel>
      </Box>
    </Fade>
  );
};
export default InventoryManagement;
